import * as React from "react";
import Box from "@mui/material/Box";
import MuiCard from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import ForgotPassword from "./ForgotPassword";
import { SitemarkIcon } from "./CustomIcons";
import { useAuth } from "../../context/auth";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNotifications } from "@toolpad/core/useNotifications";
import { useMutation } from "@tanstack/react-query";
import * as Yup from "yup";
import { API_URL } from "../../utilities";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";

// Styled Card
const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  [theme.breakpoints.up("sm")]: {
    width: "450px",
  },
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

// Yup validation schema
const validationSchema = Yup.object({
  userName: Yup.string().required("نام کاربری الزامی است"),
  password: Yup.string()
    .min(12, "پسورد باید 12 کاراکتر باشد")
    .required("پسورد الزامی است"),
});

export default function SignInCard() {
  const [open, setOpen] = React.useState(false);
  const { signIn, user } = useAuth();
  const notifications = useNotifications();
  const navigate = useNavigate();
  React.useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (body: any) => {
      const response = await fetch(API_URL + "account/login", {
        method: "POST",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(body),
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      JSON.stringify(responseData);
    },
    onError: (error) => {
      notifications.show("خطا در دریافت اطلاعات", {
        severity: "error",
        autoHideDuration: 3000,
      });
      console.log("eee", error);
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Formik hook
  const formik = useFormik({
    initialValues: {
      userName: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const { userName, password } = values;
      try {
        const result = await signIn(userName, password, mutateAsync);
        if (!result?.isOk) {
          notifications.show(result?.message, {
            severity: "error",
            autoHideDuration: 3000,
          });
          return;
        }
        notifications.show(result?.message, {
          severity: "success",
          autoHideDuration: 3000,
        });
        navigate("/");
      } catch (error) {
        console.log("🚀 ~ onSubmit: ~ error:", error);
        notifications.show("خطا در دریافت اطلاعات", {
          severity: "error",
          autoHideDuration: 3000,
        });
      }
    },
  });

  return (
    <Card variant="outlined">
      <Box sx={{ display: { xs: "flex", md: "none" } }}>
        <SitemarkIcon />
      </Box>
      <Typography
        variant="h4"
        sx={{
          width: "100%",

          textAlign: "center",
        }}
      >
        پورتال تکنو حساب
      </Typography>

      {/* Form */}
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: 2,
        }}
      >
        <FormControl>
          <TextField
            label="نام کاربری"
            placeholder="نام کاربری"
            fullWidth
            variant="outlined"
            color={
              formik.errors.userName && formik.touched.userName
                ? "error"
                : "primary"
            }
            onChange={formik.handleChange("userName")}
            onBlur={formik.handleBlur("userName")}
            value={formik.values.userName}
            error={
              (formik.errors.userName && formik.touched.userName) as boolean
            }
            helperText={formik.touched.userName && formik.errors.userName}
            required
            autoFocus
          />
        </FormControl>

        <FormControl>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
              sx={{ alignSelf: "baseline", marginBottom: 2 }}
            >
              رمز عبور خود را فراموش کرده اید؟
            </Link>
          </Box>
          <TextField
            label="پسورد"
            placeholder="پسورد"
            fullWidth
            variant="outlined"
            color={
              formik.errors.password && formik.touched.password
                ? "error"
                : "primary"
            }
            onChange={formik.handleChange("password")}
            onBlur={formik.handleBlur("password")}
            value={formik.values.password}
            error={
              (formik.errors.password && formik.touched.password) as boolean
            }
            helperText={formik.touched.password && formik.errors.password}
            required
            type="password"
          />
        </FormControl>

        <FormControlLabel
          control={<Checkbox value="remember" color="primary" />}
          label="من را به خاطر بسپار"
        />

        <ForgotPassword open={open} handleClose={handleClose} />

        <LoadingButton
          type="submit"
          loading={formik.isSubmitting || isPending}
          fullWidth
          variant="contained"
        >
          ورود
        </LoadingButton>
      </Box>
    </Card>
  );
}
